import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { Rocket, DollarSign, User, TrendingUp, GitBranch } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { UPFRONT_COMMISSION_RATE, formatCurrency } from './gameplanUtils';
import { LENDERS, CREDIT_TEAM, LOAN_PURPOSES, APPLICATION_TYPES } from './pipelineConstants';

// Return the current instant in Australia/Sydney with DST handled correctly.
// Uses Intl to derive the offset — no external tz lib on the frontend.
const sydneyNowIso = () => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
    timeZoneName: 'shortOffset',
  }).formatToParts(now).reduce((acc, p) => (acc[p.type] = p.value, acc), {});
  const off = (parts.timeZoneName || 'GMT+10').replace('GMT', '');
  const sign = off.startsWith('-') ? '-' : '+';
  const abs = off.replace(/[+-]/, '') || '10';
  const [hh, mm = '00'] = abs.split(':');
  const offset = `${sign}${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}${offset}`;
};

const sydneyToday = () => sydneyNowIso().slice(0, 10);

const AddDealDialog = ({ open, onClose, onAdd, onSave, editDeal, options, month, presetWeek }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState('essentials');
  const isEdit = !!editDeal;

  const computeDefaultDate = () => {
    const weekDay = { 1: 3, 2: 10, 3: 17, 4: 25 };
    if (month && presetWeek && weekDay[presetWeek]) {
      return `${month}-${String(weekDay[presetWeek]).padStart(2, '0')}`;
    }
    const sydneyToday_ = sydneyToday();
    if (month && !sydneyToday_.startsWith(month)) {
      return `${month}-01`;
    }
    return sydneyToday_;
  };

  const [form, setForm] = useState({
    submitted_date: computeDefaultDate(),
    submitted_at: sydneyNowIso(),
    projected_settlement_date: '',
    client_name: '',
    broker: '',
    est_loan_amount: '',
    loan_type: 'Purchase',
    lead_source: 'Brokerage',
    status_stage: 'submitted',
    ur: '',
    notes: '',
    brokerage_fee_charged: false,
    brokerage_fee_amount: '',
    lender: '',
    credit_team_member: '',
    loan_purpose: '',
    application_type: '',
    lead_received_date: '',
    portal_sent_date: '',
    portal_completed_date: '',
    proposal_date: '',
    conditional_approval_date: '',
    unconditional_approval_date: '',
  });

  React.useEffect(() => {
    if (open) {
      if (editDeal) {
        // Prefill from the existing deal so every field (incl. brokerage) is editable.
        setForm({
          submitted_date: (editDeal.submitted_date || '').slice(0, 10) || computeDefaultDate(),
          submitted_at: editDeal.submitted_at || sydneyNowIso(),
          projected_settlement_date: (editDeal.projected_settlement_date || '').slice(0, 10),
          client_name: editDeal.client_name || '',
          broker: editDeal.broker || '',
          est_loan_amount: editDeal.est_loan_amount ?? '',
          loan_type: editDeal.loan_type || 'Purchase',
          lead_source: editDeal.lead_source || 'Brokerage',
          status_stage: editDeal.status_stage || 'submitted',
          ur: editDeal.ur || '',
          notes: editDeal.notes || '',
          brokerage_fee_charged: !!editDeal.brokerage_fee_charged,
          brokerage_fee_amount: editDeal.brokerage_fee_amount ? String(editDeal.brokerage_fee_amount) : '',
          lender: editDeal.lender || '',
          credit_team_member: editDeal.credit_team_member || '',
          loan_purpose: editDeal.loan_purpose || '',
          application_type: editDeal.application_type || '',
          lead_received_date: (editDeal.lead_received_date || '').slice(0, 10),
          portal_sent_date: (editDeal.portal_sent_date || '').slice(0, 10),
          portal_completed_date: (editDeal.portal_completed_date || '').slice(0, 10),
          proposal_date: (editDeal.proposal_date || '').slice(0, 10),
          conditional_approval_date: (editDeal.conditional_approval_date || '').slice(0, 10),
          unconditional_approval_date: (editDeal.unconditional_approval_date || '').slice(0, 10),
        });
        setTab('essentials');
        return;
      }
      const brokerNames = (options?.brokers || []).map(b => b.name);
      const defaultBroker = (user?.name && brokerNames.includes(user.name)) ? user.name : '';
      setForm((prev) => ({
        ...prev,
        submitted_date: computeDefaultDate(),
        submitted_at: sydneyNowIso(),
        broker: defaultBroker || prev.broker,
      }));
      setTab('essentials');
    }
  }, [open, month, presetWeek, user?.id, editDeal?.id]);

  const handleSubmit = async () => {
    if (!form.client_name || !form.broker || !form.est_loan_amount) {
      toast.error('Please fill required fields (Client, Broker, Loan Amount)');
      setTab('essentials');
      return;
    }
    const data = {
      ...form,
      est_loan_amount: Number(form.est_loan_amount),
      projected_settlement_date: form.projected_settlement_date || null,
      brokerage_fee_charged: !!form.brokerage_fee_charged,
      brokerage_fee_amount: form.brokerage_fee_charged ? Number(form.brokerage_fee_amount || 0) : 0,
    };
    if (isEdit) {
      await onSave(editDeal.id, data);
      onClose();
      return;
    }
    await onAdd(data);
    const brokerNames = (options?.brokers || []).map(b => b.name);
    const defaultBroker = (user?.name && brokerNames.includes(user.name)) ? user.name : '';
    setForm({
      submitted_date: computeDefaultDate(),
      submitted_at: sydneyNowIso(),
      projected_settlement_date: '',
      client_name: '', broker: defaultBroker, est_loan_amount: '',
      loan_type: 'Purchase', lead_source: 'Brokerage',
      status_stage: 'submitted', ur: '', notes: '',
      brokerage_fee_charged: false, brokerage_fee_amount: '',
      lender: '', credit_team_member: '', loan_purpose: '', application_type: '',
      lead_received_date: '', portal_sent_date: '', portal_completed_date: '',
      proposal_date: '', conditional_approval_date: '', unconditional_approval_date: '',
    });
    onClose();
  };

  const getDefaultProjection = () => {
    if (form.submitted_date) {
      const date = new Date(form.submitted_date);
      date.setMonth(date.getMonth() + 2);
      return date.toISOString().slice(0, 10);
    }
    return '';
  };

  // Live revenue calc — reused inside Revenue tab
  const loanNum = Number(form.est_loan_amount) || 0;
  const upfront = loanNum * UPFRONT_COMMISSION_RATE;
  const brokerage = form.brokerage_fee_charged ? (Number(form.brokerage_fee_amount) || 0) : 0;
  const revenueTotal = upfront + brokerage;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden gap-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100 flex-shrink-0">
          <DialogTitle className="text-lg font-bold text-slate-900">{isEdit ? 'Edit Deal' : 'Add New Deal'}</DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">
            {isEdit ? 'Update any detail — including the brokerage fee on the Revenue tab.' : 'Fill essentials to save. Revenue & Pipeline fields are optional but power your analytics.'}
          </p>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 min-h-0">
          <div className="px-6 pt-3 flex-shrink-0 bg-white border-b border-slate-100">
            <TabsList className="grid grid-cols-3 w-full bg-slate-100/60 h-10">
              <TabsTrigger value="essentials" data-testid="tab-essentials" className="text-xs font-semibold flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                Essentials
              </TabsTrigger>
              <TabsTrigger value="revenue" data-testid="tab-revenue" className="text-xs font-semibold flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" />
                Revenue
              </TabsTrigger>
              <TabsTrigger value="pipeline" data-testid="tab-pipeline" className="text-xs font-semibold flex items-center gap-1.5">
                <GitBranch className="w-3.5 h-3.5" />
                Pipeline
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5" data-testid="add-deal-form-scroll">
            {/* ── ESSENTIALS TAB ─────────────────────────────────────── */}
            <TabsContent value="essentials" className="m-0 space-y-4 focus-visible:outline-none">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Submitted Date *</label>
                  <Input type="date" value={form.submitted_date} onChange={(e) => setForm({ ...form, submitted_date: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Broker *</label>
                  <Select value={form.broker} onValueChange={(v) => setForm({ ...form, broker: v })}>
                    <SelectTrigger data-testid="deal-broker-select"><SelectValue placeholder="Select broker" /></SelectTrigger>
                    <SelectContent>
                      {options.brokers.map(b => (
                        <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Client Name *</label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="deal-client-name" placeholder="e.g. John & Jane Smith" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Loan Amount *</label>
                  <Input type="number" value={form.est_loan_amount} onChange={(e) => setForm({ ...form, est_loan_amount: e.target.value })} placeholder="500000" data-testid="deal-amount" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Loan Type</label>
                  <Select value={form.loan_type} onValueChange={(v) => setForm({ ...form, loan_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {options.loanTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Lead Source</label>
                  <Select value={form.lead_source} onValueChange={(v) => setForm({ ...form, lead_source: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {options.leadSources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Status</label>
                  <Select value={form.status_stage} onValueChange={(v) => setForm({ ...form, status_stage: v })}>
                    <SelectTrigger data-testid="deal-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending_submission">Pending Submission</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">UR (Reference)</label>
                <Input value={form.ur} onChange={(e) => setForm({ ...form, ur: e.target.value })} placeholder="Optional lender reference" />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Notes</label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Anything worth remembering about this file…" />
              </div>
            </TabsContent>

            {/* ── REVENUE TAB ────────────────────────────────────────── */}
            <TabsContent value="revenue" className="m-0 space-y-4 focus-visible:outline-none">
              <div className="rounded-lg p-4 border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-emerald-700" />
                  <label className="text-sm font-semibold text-emerald-900">Estimated Revenue</label>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700/70">
                    Upfront {(UPFRONT_COMMISSION_RATE * 100).toFixed(2)}% + brokerage
                  </span>
                </div>

                <p className="text-xs font-medium text-emerald-900/80 mb-2">Is a brokerage fee being charged?</p>
                <div className="flex gap-2 mb-3">
                  {[
                    { value: false, label: 'No' },
                    { value: true, label: 'Yes' },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        brokerage_fee_charged: opt.value,
                        brokerage_fee_amount: opt.value ? form.brokerage_fee_amount : '',
                      })}
                      data-testid={`brokerage-fee-${opt.label.toLowerCase()}`}
                      className={
                        'flex-1 rounded-md px-3 py-1.5 text-sm font-bold transition-all border ' +
                        (form.brokerage_fee_charged === opt.value
                          ? 'bg-emerald-600 border-emerald-700 text-white shadow'
                          : 'bg-white border-emerald-200 text-emerald-900 hover:bg-emerald-100')
                      }
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {form.brokerage_fee_charged && (
                  <div className="mb-3">
                    <label className="text-xs font-semibold text-emerald-900 mb-1 block">Brokerage Fee Amount ($)</label>
                    <Input
                      type="number"
                      min="0"
                      value={form.brokerage_fee_amount}
                      onChange={(e) => setForm({ ...form, brokerage_fee_amount: e.target.value })}
                      placeholder="e.g. 1500"
                      data-testid="brokerage-fee-amount"
                      className="bg-white"
                    />
                  </div>
                )}

                <div
                  className="rounded-md bg-white/70 border border-emerald-100 px-3 py-2 grid grid-cols-3 gap-2 text-center"
                  data-testid="deal-revenue-preview"
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700/70 font-bold">Upfront</div>
                    <div className="font-bold text-emerald-900 text-sm tabular-nums">{formatCurrency(upfront)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700/70 font-bold">Brokerage</div>
                    <div className="font-bold text-emerald-900 text-sm tabular-nums">{formatCurrency(brokerage)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700/70 font-bold">Est. Total</div>
                    <div className="font-black text-emerald-700 text-sm tabular-nums" data-testid="deal-revenue-total">
                      {formatCurrency(revenueTotal)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <Rocket className="w-4 h-4 text-blue-600" />
                  <label className="text-sm font-semibold text-blue-800">Projected Settlement Date</label>
                </div>
                <p className="text-xs text-blue-600 mb-2">Set when you expect this deal to settle (appears in Projections)</p>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={form.projected_settlement_date}
                    onChange={(e) => setForm({ ...form, projected_settlement_date: e.target.value })}
                    className="flex-1 bg-white"
                    data-testid="deal-projected-settlement"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setForm({ ...form, projected_settlement_date: getDefaultProjection() })}
                    className="text-xs"
                  >
                    +2 Months
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ── PIPELINE TAB ───────────────────────────────────────── */}
            <TabsContent value="pipeline" className="m-0 space-y-5 focus-visible:outline-none">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">File Attribution</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Lender / Bank</label>
                    <Select value={form.lender || 'none'} onValueChange={(v) => setForm({ ...form, lender: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="deal-lender"><SelectValue placeholder="Select lender" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {LENDERS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Credit Team Member</label>
                    <Select value={form.credit_team_member || 'none'} onValueChange={(v) => setForm({ ...form, credit_team_member: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="deal-credit-team"><SelectValue placeholder="Assign credit officer" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Unassigned —</SelectItem>
                        {CREDIT_TEAM.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Loan Purpose</label>
                    <Select value={form.loan_purpose || 'none'} onValueChange={(v) => setForm({ ...form, loan_purpose: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="deal-loan-purpose"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {LOAN_PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Application Type</label>
                    <Select value={form.application_type || 'none'} onValueChange={(v) => setForm({ ...form, application_type: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="deal-application-type"><SelectValue placeholder="Select app type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {APPLICATION_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Journey Dates</p>
                <p className="text-[11px] text-slate-500 mb-3">Each date marks entry into that stage — powers time-at-stage &amp; turnaround analytics.</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['Lead received',           'lead_received_date'],
                    ['Portal sent',             'portal_sent_date'],
                    ['Portal completed',        'portal_completed_date'],
                    ['Proposal prepared',       'proposal_date'],
                    ['Conditional approval',    'conditional_approval_date'],
                    ['Unconditional approval',  'unconditional_approval_date'],
                  ].map(([label, field]) => (
                    <div key={field}>
                      <label className="text-xs font-semibold text-slate-700 mb-1 block">{label}</label>
                      <Input
                        type="date"
                        value={form[field]}
                        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                        data-testid={`deal-${field.replace(/_/g,'-')}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="px-6 py-3 border-t border-slate-100 bg-slate-50/70 flex-shrink-0 flex sm:justify-between sm:items-center gap-2">
          <div className="text-[11px] text-slate-500 hidden sm:block">
            {tab === 'essentials' && 'Step 1 of 3 — Client basics'}
            {tab === 'revenue' && 'Step 2 of 3 — Revenue & settlement'}
            {tab === 'pipeline' && 'Step 3 of 3 — Bank & journey (optional)'}
          </div>
          <div className="flex gap-2">
            {tab !== 'essentials' && (
              <Button
                variant="outline"
                onClick={() => setTab(tab === 'pipeline' ? 'revenue' : 'essentials')}
                data-testid="deal-tab-back"
              >
                Back
              </Button>
            )}
            {tab !== 'pipeline' ? (
              <Button
                onClick={() => setTab(tab === 'essentials' ? 'revenue' : 'pipeline')}
                data-testid="deal-tab-next"
              >
                Next
              </Button>
            ) : null}
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="submit-deal-btn" className="bg-slate-900 hover:bg-slate-800">
              {isEdit ? 'Save Changes' : 'Add Deal'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { AddDealDialog };
export default AddDealDialog;
